import publicIp from 'public-ip';
import { useCallback, useEffect, useState } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { PageUrls } from '../../App';
import { Provinces, States } from '../../constants';
import { useSession } from '../../hooks/useSession';
import { logFetchError } from '../../services/datadog';
import {
    CountryAbbrvs,
    IBook,
    ISelectOption,
    IUserAccount,
    IUserAccountRegion,
    UnsupportedReason
} from '../../types';
import SelectLocations from './select-locations';
import SelectSportsbook from './select-sportsbook';

export interface IBookSelectOption {
    label: string;
    value: string;
    bookRegionId: string | undefined;
    isDisabled: boolean;
    disabledText: string;
}

export interface SelectSportsbookProps {
    getUnsupportedReason: (
        book: IBook | undefined,
        userAccountRegion: IUserAccountRegion | undefined
    ) => UnsupportedReason;
}

const getCurrentState = async () => {
    function inRange(x: number, min: number, max: number) {
        return x >= min && x <= max;
    }
    const ip = await publicIp.v4();
    var url = `https://api.ipstack.com/${ip}?access_key=37a7faaa40222eec402c67e1f6a154c8`;
    try {
        var r = await fetch(url);
    } catch (err) {
        logFetchError(url, null, err);
        return;
    }

    if (r.status > 299 || r.status < 200) {
        logFetchError(url, r.status, null);
        return;
    }

    var data = await r.json();
    let zip = data['zip'];

    let state = States.find((x) =>
        inRange(zip, x.zipCodeBoundaries[0][0], x.zipCodeBoundaries[0][1])
    );
    if (!state) {
        return null;
    } else {
        return { label: state.label, value: state.value.toLocaleLowerCase() };
    }
};

// Option constants
const usaCountryOption = { label: 'United States', value: CountryAbbrvs.USA };
const otherCountryOption = { label: 'Canada', value: CountryAbbrvs.CA };
export const countryOptions: ISelectOption[] = [
    usaCountryOption,
    otherCountryOption,
];

//get the regions to display in the dropdown
const getDisplayRegions = (selectedCountry: ISelectOption): ISelectOption[] => {
    let Regions =
        selectedCountry?.value === CountryAbbrvs.USA ? States : Provinces;
    return Regions.map(({ label, value }) => ({
        label,
        value: value.toLowerCase(),
    }));
};

export interface ISelectRoot {
    getUnsupportedReason: (
        book: IBook | undefined,
        userAccountRegion: IUserAccountRegion | undefined) => UnsupportedReason;
}

export default function SelectRoot(props: ISelectRoot) {
    const { getUnsupportedReason } = props;

    const session = useSession();
    const { params } = useRouteMatch<{ cid?: string; state?: string }>();

    const [bookOptions, setBookOptions] = useState<IBookSelectOption[]>([]);
    const [selectedBookOption, setSelectedBookOption] =
        useState<IBookSelectOption>();

    const [selectedCountry, setSelectedCountry] =
        useState<ISelectOption | null>(usaCountryOption);
    const [selectedState, setSelectedState] = useState<ISelectOption | null>(
        null
    );

    const currentState = selectedState?.value || params.state || '';

    // Human-readable state name for the sportsbook subhead. Prefer the selected
    // option's label; otherwise resolve the lowercase abbr via the region
    // constants; fall back to the abbr itself.
    const currentStateLabel =
        selectedState?.label ||
        [...States, ...Provinces].find(
            (region) => region.value.toLowerCase() === currentState
        )?.label ||
        currentState;

    const getStates = useCallback(async () => {
        if (!currentState) {
            const state = await getCurrentState();
            if (state) {
                setSelectedState(state);
            }
        }
    }, [currentState]);

    useEffect(function () {
        getStates();
    }, []);

    // util method: get user account region based on selected country and state options
    const getUserAccountRegionForSelectedLocation = (
        userAccountRegions: IUserAccountRegion[] | undefined
    ): IUserAccountRegion | undefined => {
        return userAccountRegions?.find(
            ({ bookRegion }: IUserAccountRegion) => {
                return bookRegion.abbr === currentState;
            }
        );
    };

    //Map useraccounts to bookSelection options
    function getBookSelectionOption(
        userAccount: IUserAccount
    ): IBookSelectOption {
        //find selected userAccountRegion
        const userAccountRegionSelected = userAccount.userAccountRegions.find(
            ({ bookRegion }: IUserAccountRegion) => {
                return bookRegion.abbr === currentState;
            }
        );

        //get status of selected bookRegion
        const bookRegionStatus = userAccountRegionSelected?.bookRegion.status;

        var label = userAccount.book.name;
        var isDisabled = false;
        let disabledText = '';
        if (bookRegionStatus == 'coming'){
            disabledText = 'Coming Soon'
            isDisabled = true
          } else if (bookRegionStatus == 'inactive'){
            disabledText = 'Down for Maintenence'
            isDisabled = true
          }

        return {
            label: label,
            bookRegionId: userAccountRegionSelected?.bookRegion.id,
            value: userAccount.book.abbr,
            isDisabled: isDisabled,
            disabledText: disabledText
        };
    }

    //get the books to display in the dropdown
    const getBookOptions = (): IBookSelectOption[] => {
        session?.userAccounts?.forEach((ua: IUserAccount) => {
            console.log(`[DEBUG] userAccount: book=${ua.book.abbr}, name=${ua.book.name}, active=${ua.active}, sdkRequired=${ua.book.sdkRequired}, regions=[${ua.userAccountRegions?.map((uar: IUserAccountRegion) => uar.bookRegion.abbr).join(', ')}]`);
        });
        console.log('[DEBUG] currentState:', currentState);

        //filter out userAccount that are turned off by user
        var userAccounts = session?.userAccounts?.filter(
            ({ active, userAccountRegions, book }: IUserAccount) => {
                // check for books turned off at top level
                if (!active) {
                    console.log(`[DEBUG] ${book.abbr} (${book.name}) filtered out: active=false`);
                    return false;
                }

                // check for books turned off at region level
                const userAccountRegion =
                    getUserAccountRegionForSelectedLocation(userAccountRegions);
                if (userAccountRegion?.active === false) {
                    console.log(`[DEBUG] ${book.abbr} (${book.name}) filtered out: region-level active=false`);
                    return false;
                }

                // default is to keep the book as an option
                return true;
            }
        );

        if (!userAccounts) {
            console.log('[DEBUG] No userAccounts found at all');
            return [];
        }

        //Filter out books that are unsupported in the selected state
        //These userAccountRegions are not present on the UserAccount object
        userAccounts = userAccounts.filter(
            ({ userAccountRegions, book }: IUserAccount) => {
                const supportedBooks = userAccountRegions.map(
                    (userAccountRegion) => userAccountRegion.bookRegion.abbr
                );
                if (supportedBooks.includes(currentState)) {
                    return true;
                } else {
                    console.log(`[DEBUG] ${book.abbr} (${book.name}) filtered out: state '${currentState}' not in supported regions [${supportedBooks.join(', ')}]`);
                    return false;
                }
            }
        );

        //Dont filter if extensionAuthToken is present or if user has integrated mobile package
        //otherwise filter out sdkRequired books
        if (!session?.extensionAuthToken && !session?.user?.mobilePackage){
            userAccounts = userAccounts.filter(u => {
                if (u.book.sdkRequired) {
                    console.log(`[DEBUG] ${u.book.abbr} (${u.book.name}) filtered out: sdkRequired=true (no extension/mobile)`);
                }
                return u.book.sdkRequired == false;
            });
        }

        //map to bookSelections, order by isDisabled
        return userAccounts.map(getBookSelectionOption).sort(function (x, y) {
            return Number(x.isDisabled) - Number(y.isDisabled);
        })
    };

    //set new bookOptions when changing State
    useEffect(
        function () {
            setBookOptions(getBookOptions());
        },
        [selectedState]
    );

    //When country changes also change seleted state to null
    useEffect(
		function () {
			setSelectedState(null);
		},
		[selectedCountry]
	)

    return (
        <Switch>
            <Route
                path={PageUrls.LOCATION_SELECT}
                render={() => (
                    <SelectLocations
                        selectedState={selectedState}
                        selectedCountry={selectedCountry}
                        setSelectedCountry={setSelectedCountry}
                        setSelectedState={setSelectedState}
                        getDisplayRegions={getDisplayRegions}
                        countryOptions={countryOptions}
                        bookOptions={bookOptions}
                    />
                )}
            />
            <Route
                path={PageUrls.BOOK_LIST}
                render={() => (
                    <SelectSportsbook
                        currentState={currentState}
                        stateLabel={currentStateLabel}
                        selectedBookOption={selectedBookOption}
                        setSelectedBookOption={setSelectedBookOption}
                        bookOptions={bookOptions}
                    />
                )}
            />
        </Switch>
    );
}
